import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardBody } from "../../components/Card/CardBody";
import { Card } from "../../components/Card/Card";
import { RightsFilters, RightsFiltersParams } from "./RightsFilters";
import { CreateRightsRequest, RevokeRightsRequest, TrackReleaseRight, useRightsApi } from "../../api/useRightsApi";
import { MemoizedTable } from "../../components/Table/Table";
import { rightsColumnsDefinition } from "./RightsColumnsDefinition";
import { Preloader } from "../../components/Preloader";
import { useApiExecutor } from "../../hooks/useApiExecutor";
import { Button } from "../../components/Button";
import { TABLE_SELECT_COMPOSITE_KEY_SEPARATOR, useTableSelect } from "../../hooks/use-table-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { SimpleViewModal } from "../../components/Modals/SimpleViewModal";
import { ValidationInputWrapper } from "../../components/ValidationInputWrapper";
import { Input } from "../../components/Input";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { useAdvancedState } from "../../hooks/use-advanced-state";
import { InputMultiSelect } from "../../components/InputMultiSelect";
import { Country } from "../../api/useMusicApi";
import { useContractors } from "../../contexts/ContractorsContext";
import { InputSelect } from "../../components/InputSelect";
import { InputLabel } from "../../components/InputLabel";
import { AxiosError } from "axios";
import { useValidationErrors } from "../../hooks/use-validation-errors";
import { useLoading } from "../../hooks/use-loading";
import { toast } from "react-toastify";
import { TextArea } from "../../components/TextArea";
import { faFile } from "@fortawesome/free-solid-svg-icons/faFile";
import { useFilePicker } from "use-file-picker";

export const RightsPage = () => {
  const { t } = useTranslation();
  const api = useRightsApi();
  const { contractors } = useContractors();
  const rightPercentInputRef = useMaskito({
    options: maskitoNumberOptionsGenerator({
      max: 100,
      min: 0,
      precision: 2,
      decimalSeparator: "."
    })
  });

  const { loadings, startLoading, stopLoading } = useLoading({
    createRights: false,
    revokeRights: false
  });

  const [isModalCreateRightVisible, setIsModalCreateRightVisible] = useState<boolean>(false);
  const [isModalRevokeRightVisible, setIsModalRevokeRightVisible] = useState<boolean>(false);

  const { info, contents, fetch, fetchMore } = useApiExecutor<
    TrackReleaseRight,
    RightsFiltersParams
  >({
    showInPage: 100,
    fetchCallback: api.getRights
  });

  const { selectedContents: selectedCompositeKeys, handleRowSelect, resetSelectedItems } = useTableSelect(
    ["release_id", "work_contractor"],
    contents
  );

  const [createForm, , updateCreateForm, resetCreateForm] = useAdvancedState<CreateRightsRequest>({
    share: 0,
    external_id: "",
    country_list: [],
    authors_names: [],
    issue_date: null,
    expire_date: null,
    contractor_id: null,
    release_id_list: []
  });

  const countriesOptions = useMemo(() => {
    const selectedCountries = createForm?.country_list ?? [];
    if (selectedCountries.length === 0) {
      return Object.keys(Country).map((g) => ({ label: t(g), value: g }));
    }

    if (selectedCountries.includes(Country.ALL)) {
      return [{ value: Country.ALL, label: t(Country.ALL) }];
    }

    return Object.keys(Country)
      .filter(c => c !== Country.ALL)
      .map((g) => {
        return ({ label: t(g), value: g });
      });
  }, [createForm?.country_list]);

  const [revokeForm, , updateRevokeForm, resetRevokeForm] = useAdvancedState<RevokeRightsRequest>({
    expire_date: null,
    rights_items: []
  });

  const {
    validationErrors: createValidationErrors,
    setValidationErrors: setCreateValidationErrors
  } = useValidationErrors<CreateRightsRequest>(createForm);
  const {
    validationErrors: revokeValidationErrors,
    setValidationErrors: setRevokeValidationErrors
  } = useValidationErrors<RevokeRightsRequest>(revokeForm);

  useEffect(() => {
    if (contractors.length === 1) {
      updateCreateForm({ contractor_id: contractors[0].id });
    }
  }, [contractors.length]);

  const [open] = useFilePicker({
    accept: [".xlsx"],
    multiple: false,
    onFilesSuccessfulySelected: async (data) => {
      await toast.promise<any, AxiosError>(api.putUploadRightsExcelFile(data.plainFiles[0]), {
        pending: t("upload_rights_pending").toString(),
        success: t("upload_rights_success").toString(),
        error: {
          render: props => {
            const validationError = ((props.data as AxiosError).response?.data as any)?.errors ?? [];
            return validationError[0]?.detail ?? t("something_went_wrong");
          }
        }
      });
    }
  });

  const handleCreateRight = useCallback(async () => {
    try {
      startLoading("createRights");
      await api.createRights({
        ...createForm,
        release_id_list: selectedCompositeKeys.map(compositeKey => {
          const [releaseId] = compositeKey.toString().split(TABLE_SELECT_COMPOSITE_KEY_SEPARATOR);
          return releaseId;
        })
      });
      toast.success(t("create_rights.success_create"));
      setIsModalCreateRightVisible(false);
      if (resetSelectedItems != null) {
        resetSelectedItems({});
      }
      resetCreateForm();
      setCreateValidationErrors([], createForm);
    } catch (e) {
      setCreateValidationErrors(
        ((e as AxiosError).response?.data as any)?.errors ?? [],
        createForm as any
      );
    } finally {
      stopLoading("createRights");
    }

  }, [createForm, selectedCompositeKeys]);

  const handleRevokeRight = useCallback(async () => {
    try {
      startLoading("revokeRights");
      await api.revokeRights({
        ...revokeForm,
        rights_items: selectedCompositeKeys.map(compositeKey => {
          const [releaseId, workContractor] = compositeKey.toString().split(TABLE_SELECT_COMPOSITE_KEY_SEPARATOR);
          const item = contents.find(c => c.release_id == releaseId && c.work_contractor == workContractor);
          return {
            release_id: item?.release_id,
            work_contractor: item?.work_contractor
          };
        }) as any
      });
      toast.success(t("revoke_rights.success_revoke"));
      setIsModalRevokeRightVisible(false);
      setRevokeValidationErrors([], revokeForm);
      if (resetSelectedItems != null) {
        resetSelectedItems({});
      }
      resetRevokeForm();
    } catch (e) {
      setRevokeValidationErrors(
        ((e as AxiosError).response?.data as any)?.errors ?? [],
        revokeForm as any
      );
    } finally {
      stopLoading("revokeRights");
    }

  }, [revokeForm, contents, selectedCompositeKeys]);

  const columnsDefinitions = useMemo(() => {
    return rightsColumnsDefinition(t) as any;
  }, []);

  return (
    <>
      <div className={"w-full border-solid py-3 xl:px-8 sm:px-4 border-2 border-gray-200 rounded-r rounded-b"}>
        <div className={"mb-3"}>
        </div>

        <div className={"flex justify-between items-center flex-wrap mb-3 gap-2"}>
          <RightsFilters onApply={fetch} />
          <div className={"flex items-center gap-2"}>
            <Button className={"text-xs py-2.5 font-medium"} onClick={open}>
              <FontAwesomeIcon
                icon={faFile}
                className="w-3 h-3 mr-2 fill-current"
              />
              {t("upload_rights")}
            </Button>
            <div>
              <button
                type="button"
                disabled={selectedCompositeKeys.length === 0}
                className="inline-flex items-center px-3 py-2.5 text-xs dark:text-white font-medium text-gray-900 bg-transparent border border-gray-900 rounded-l-md disabled:opacity-50 cursor-pointer hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
                onClick={() => setIsModalCreateRightVisible(true)}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  className="w-3 h-3 mr-2 fill-current"
                />
                {t("add")}
              </button>
              <button
                type="button"
                disabled={selectedCompositeKeys.length === 0 || selectedCompositeKeys.find(compositeKey => {
                  const [releaseId, workContractor] = compositeKey.toString().split(TABLE_SELECT_COMPOSITE_KEY_SEPARATOR);
                  const item = contents.find(c => c.release_id == releaseId && c.work_contractor == workContractor);
                  return item?.share == null;
                })}
                className="inline-flex items-center px-3 py-2.5 text-xs dark:text-white font-medium text-gray-900 bg-transparent border border-gray-900 rounded-r-md disabled:opacity-50 cursor-pointer hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
                onClick={() => setIsModalRevokeRightVisible(true)}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  className="w-3 h-3 mr-2 fill-current"
                />
                {t("revoke")}
              </button>
            </div>
          </div>
        </div>
        <div className={"flex justify-between items-center flex-wrap mb-3 gap-2"}>
          <Card className={"w-full"}>
            <CardBody>
              {info?.isPageLoading || info?.isInitialLoading ? (
                <Preloader size={"3x"} className={"text-muted"} />
              ) : (
                <MemoizedTable
                  data={contents}
                  columnsDefinition={columnsDefinitions}
                  onRowSelectionChange={handleRowSelect as any}
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>
      {info?.canLoadMore && !info?.isPageLoading && !info?.isInitialLoading && (
        <div className={"text-center"}>
          <Button
            loading={info?.isMoreLoading}
            label={"Загрузить еще"}
            onClick={fetchMore}
          />
        </div>
      )}

      {isModalCreateRightVisible &&
        <SimpleViewModal
          title={t("create_rights.modal_title")}
          onSuccess={handleCreateRight}
          disableSuccess={loadings.createRights}
          className={"w-1/3"}
          onCancel={() => {
            setIsModalCreateRightVisible(false);
            resetCreateForm();
            setCreateValidationErrors([], createForm);
          }}
        >
          <ValidationInputWrapper
            attribute={`share`}
            className={"mb-2"}
            validationErrors={createValidationErrors}
          >
            <Input
              required
              ref={rightPercentInputRef}
              validationError={createValidationErrors.find((v) => v.attr === `share`)?.detail ?? null}
              onWheel={(e) => (e.target as any).blur()}
              className={"mt-1"}
              label={`${t("copyright_share")}`}
              value={createForm.share ?? undefined}
              onInput={(e) => {
                const value = e.currentTarget.value;
                if (value === "") {
                  updateCreateForm({ share: null });
                  return;
                }

                updateCreateForm({ share: value });
              }}
              onChange={(value) => undefined}
            />
          </ValidationInputWrapper>
          <ValidationInputWrapper
            attribute={"external_id"}
            className={"mb-2"}
            validationErrors={createValidationErrors}
          >
            <Input
              required
              className={"mt-1"}
              validationError={createValidationErrors.find((v) => v.attr === `external_id`)?.detail ?? null}
              label={`External ID (${t("author_external_id_suggest")})`}
              defaultValue={createForm?.external_id ?? ""}
              onChange={(value) => updateCreateForm({ external_id: value })}
            />
          </ValidationInputWrapper>
          <ValidationInputWrapper
            className={"mb-2"}
            attribute={"authors_names"}
            validationErrors={createValidationErrors}
          >
            <TextArea
              placeholder={""}
              label={`${t("create_rights.authors")}`}
              onChange={(v) => {
                let value;
                if (v.trim().length === 0) {
                  value = null;
                } else if (
                  v.split("\n").filter((x) => x.trim().length > 0).length === 0
                ) {
                  value = null;
                } else {
                  value = v.split("\n");
                }

                updateCreateForm({ authors_names: value });
              }}
            />
          </ValidationInputWrapper>
          <ValidationInputWrapper
            className={"mb-2"}
            attribute={"issue_date"}
            validationErrors={createValidationErrors}
          >
            <Input
              required
              className={"my-1"}
              validationError={createValidationErrors.find((v) => v.attr === `issue_date`)?.detail ?? null}
              label={`${t("create_rights.issue_date")}`}
              value={createForm?.issue_date ?? undefined}
              onChange={(value) => updateCreateForm({ issue_date: value })}
              type={"date"}
            />
          </ValidationInputWrapper>
          <ValidationInputWrapper
            className={"mb-2"}
            attribute={"expire_date"}
            validationErrors={createValidationErrors}
          >
            <Input
              className={"mt-1"}
              validationError={createValidationErrors.find((v) => v.attr === `expire_date`)?.detail ?? null}
              label={`${t("create_rights.expire_date")}`}
              value={createForm?.expire_date ?? undefined}
              onChange={(value) => updateCreateForm({ expire_date: value })}
              type={"date"}
            />
          </ValidationInputWrapper>
          <ValidationInputWrapper
            className={"mb-2"}
            attribute={"country_list"}
            validationErrors={createValidationErrors}
          >
            <InputLabel required label={`${t("create_rights.country_list")}`} />
            <InputMultiSelect
              required
              isClearable
              placeholder={""}
              className={"mt-1"}
              hasError={createValidationErrors.find((v) => v.attr === `country_list`)?.detail != null}
              defaultValues={createForm?.country_list ?? []}
              options={countriesOptions}
              onChange={(items) => {
                updateCreateForm({
                  country_list: items.map((i) => i.value)
                });
              }}
            />
          </ValidationInputWrapper>
          {contractors.length > 1 && (
            <>
              <ValidationInputWrapper
                attribute={"contractor_id"}
                validationErrors={createValidationErrors}
              >
                <InputLabel required label={t("contractor").toString()} />
                <InputSelect
                  isClearable
                  className={"mt-1"}
                  hasError={createValidationErrors.find((v) => v.attr === `contractor_id`)?.detail != null}
                  placeholder={t("contractor").toString()}
                  value={createForm.contractor_id ?? null}
                  options={contractors.map((c) => {
                    return {
                      value: c.id,
                      label: c.title
                    };
                  })}
                  onChange={(v) => updateCreateForm({ contractor_id: v?.value as number })}
                />
              </ValidationInputWrapper>
            </>
          )}
          <div className={"rounded border p-2 mt-3"}>
            {t("create_rights.description")}
          </div>
        </SimpleViewModal>
      }

      {isModalRevokeRightVisible &&
        <SimpleViewModal
          title={t("revoke_rights.modal_title")}
          disableSuccess={loadings.revokeRights}
          onSuccess={handleRevokeRight}
          className={"w-1/3"}
          onCancel={() => {
            setIsModalRevokeRightVisible(false);
            resetRevokeForm();
            setRevokeValidationErrors([], revokeForm);
          }}
        >
          <ValidationInputWrapper
            className={"mb-2"}
            attribute={"expire_date"}
            validationErrors={revokeValidationErrors}
          >
            <Input
              required
              className={"mt-1"}
              validationError={revokeValidationErrors.find((v) => v.attr === `expire_date`)?.detail ?? null}
              label={`${t("create_rights.expire_date")}`}
              value={revokeForm?.expire_date ?? undefined}
              onChange={(value) => updateRevokeForm({ expire_date: value })}
              type={"date"}
            />
          </ValidationInputWrapper>
        </SimpleViewModal>}
    </>
  );
};
